import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { canAnimate } from "./motion";

const WORD = "workers";
const COUNT = 64;
const LIFE = 14000;
let greeted = false;

// Easter egg: type "workers" anywhere and a swarm of digital workers follows you around the page.
export default function WorkerSwarm() {
  const canvasRef = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!canAnimate()) return undefined;
    if (!greeted) {
      greeted = true;
      // eslint-disable-next-line no-console
      console.log(
        "%cGrupaAI%c\nYou found the engine room. Type “workers” anywhere on the page.",
        "font: 700 22px Manrope, sans-serif; color: #191919",
        "font: 500 13px Manrope, sans-serif; color: #6a6a6a",
      );
    }
    let typed = "";
    const onKey = (e) => {
      if (e.target.closest?.("input, textarea, select, [contenteditable]") || e.key.length !== 1) return;
      typed = (typed + e.key.toLowerCase()).slice(-WORD.length);
      if (typed === WORD) {
        typed = "";
        setActive(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!active || !canvas) return undefined;
    const ctx = canvas.getContext?.("2d");
    if (!ctx) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    const size = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    const cursor = { x: w / 2, y: h / 2 };
    const agents = Array.from({ length: COUNT }, (_, n) => {
      const edge = n % 4;
      return {
        x: edge === 0 ? -20 : edge === 1 ? w + 20 : Math.random() * w,
        y: edge === 2 ? -20 : edge === 3 ? h + 20 : Math.random() * h,
        vx: 0,
        vy: 0,
        orbit: 36 + Math.random() * 120,
        spin: (Math.random() < 0.5 ? -1 : 1) * (0.0006 + Math.random() * 0.0012),
        phase: Math.random() * Math.PI * 2,
      };
    });
    const start = performance.now();
    let raf = 0;
    const onMove = (e) => {
      cursor.x = e.clientX;
      cursor.y = e.clientY;
    };
    const tick = (t) => {
      const age = t - start;
      const leaving = age > LIFE;
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = "#191919";
      let inside = 0;
      for (const a of agents) {
        const angle = a.phase + t * a.spin;
        const tx = leaving ? a.x + (a.x - cursor.x) * 2 : cursor.x + Math.cos(angle) * a.orbit;
        const ty = leaving ? a.y + (a.y - cursor.y) * 2 : cursor.y + Math.sin(angle) * a.orbit * 0.7;
        let ax = (tx - a.x) * 0.012;
        let ay = (ty - a.y) * 0.012;
        for (const b of agents) {
          if (b === a) continue;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 256 && d2 > 0) {
            ax += dx / d2;
            ay += dy / d2;
          }
        }
        a.vx = (a.vx + ax) * 0.94;
        a.vy = (a.vy + ay) * 0.94;
        const speed = Math.hypot(a.vx, a.vy);
        if (speed > 11) {
          a.vx *= 11 / speed;
          a.vy *= 11 / speed;
        }
        a.x += a.vx;
        a.y += a.vy;
        if (a.x > -30 && a.x < w + 30 && a.y > -30 && a.y < h + 30) inside += 1;
        ctx.save();
        ctx.translate(a.x, a.y);
        ctx.rotate(Math.atan2(a.vy, a.vx));
        ctx.beginPath();
        ctx.moveTo(7, 0);
        ctx.lineTo(-5, -5);
        ctx.lineTo(-2, 0);
        ctx.lineTo(-5, 5);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      if (leaving && (!inside || age > LIFE + 4000)) {
        setActive(false);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("resize", size);
    document.documentElement.classList.add("is-swarming");
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("resize", size);
      document.documentElement.classList.remove("is-swarming");
    };
  }, [active]);
  if (!active) return null;
  return createPortal(
    <>
      <canvas ref={canvasRef} className="bm-swarm" aria-hidden="true" />
      <div className="bm-swarm-toast" role="status">
        <b>Swarm mode</b> {COUNT} digital workers deployed
      </div>
    </>,
    document.body,
  );
}
